import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Card, Col, Container, Row, Table } from "react-bootstrap";

import { useDispatch, useSelector } from "react-redux";

import { closeModal, setActiveModal } from "@/features/ui/uiSlice";

import {
  resetFilters,
  setPrimaryFilters,
} from "@/features/filters/filtersSlice";

import { RootState } from "@/app/store";

import ReleaseRow from "@/features/releases/components/ReleaseRow";
import {
  useActivateReleaseMutation,
  useGetReleasesQuery,
} from "@/features/releases/releasesApi";
import toast from "react-hot-toast";

import BrandButton from "@/common/BrandButton/BrandButton";
import DateRangePicker from "@/common/DateRangePicker/DateRangePicker";
import FilterSelect from "@/common/FilterSelect/FilterSelect";
import FormItem from "@/common/FormItem/FormItem";
import LoadingOverlay from "@/common/LoadingOverlay/LoadingOverlay";
import PageTitle from "@/common/PageTitle/PageTitle";

import { FileAddOutlined, FilterOutlined } from "@ant-design/icons";

import { Checkbox, CheckboxProps, Form } from "antd";

import { formatKkAppActiveLabelWithCode } from "../activeReleaseLabel";
import { Release } from "../types";

import dayjs from "dayjs";

import Icon from "@/assets/images/icons";
import TableRowNoResults from "@/common/TableRowNoResults/TableRowNoResults";
import Text from "@/common/Text/Text";
import PaginationRow from "@/components/PaginationRow/PaginationRow";
import ReleasesDeleteModal from "@/features/ui/modals/release-delete-modal/ReleasesDeleteModal";
import ReleaseMakeActiveModal from "@/features/ui/modals/release-make-active-modal/ReleaseMakeActiveModal";
import ReleaseSendProductionModal from "@/features/ui/modals/release-send-production-modal/ReleaseSendProductionModal";
import ReleasesControlPanelModal from "@/features/ui/modals/releases-control-panel/ReleasesControlPanelModal";
import { ActiveModalNameEnum } from "@/features/ui/types";
import styles from "./ReleasesListStyles.module.scss";

const tableHeaders = [
  {
    name: "",
    key: "checkbox",
  },
  {
    name: "App",
    key: "app",
  },
  {
    name: "Version",
    key: "version",
  },
  {
    name: "Created",
    key: "created",
  },
  {
    name: "Release Notes",
    key: "release",
  },
  {
    name: "Status",
    key: "status",
  },
  {
    name: "Actions",
    key: "actions",
  },
];
interface InitialFormValues {
  type: string | null;
  playTimeStart: string | null;
  playTimeEnd: string | null;
  status: string | null;
}

const initialFormValues: InitialFormValues = {
  type: null,
  playTimeStart: null,
  playTimeEnd: null,
  status: null,
};

const ReleasesList: React.FC = () => {
  const dispatch = useDispatch();

  const [page, setPage] = useState<number>(1);

  const [checkedList, setChecked] = useState<Release[]>([]);

  const [checkAll, setCheckAll] = useState<boolean>(false);

  const activeModalState = useSelector(
    (state: RootState) => state.ui.activeModalState
  );

  const primaryFilters = useSelector(
    (state: any) => state.filters.primaryFilters
  );

  const [form] = Form.useForm();

  const {
    data,
    isLoading: isLoadingReleases,
    isFetching: isFetchingReleases,
  } = useGetReleasesQuery({ params: { ...primaryFilters } });

  const [activateRelease, { isLoading: isActivating }] =
    useActivateReleaseMutation();

  const releases: Release[] = useMemo(() => {
    if (data?.items === undefined) {
      return [];
    }
    return data.items.map((item) => ({
      ...item,
      // Normalize backend camelCase for existing row components.
      created_at: item.createdAt || item.created_at || "",
      version: item.versionName || item.version,
      android_id: item.android_id || "",
    }));
  }, [data]);

  const activeKrosskastLabel = useMemo(
    () => formatKkAppActiveLabelWithCode(data?.active),
    [data]
  );

  const selectedForActivate = useMemo(() => {
    const id = activeModalState.ids?.[0];
    if (!id) return null;
    return releases.find((r) => r.id === id) || null;
  }, [activeModalState.ids, releases]);

  const handleConfirmMakeActive = async () => {
    if (!selectedForActivate?.id) return;
    try {
      await activateRelease(selectedForActivate.id).unwrap();
      toast.success("Release activated (approved-artifact pointer updated)");
      dispatch(closeModal());
    } catch (err: any) {
      const message =
        err?.data?.message || err?.error || "Failed to activate release";
      toast.error(String(message));
    }
  };
  const handleOnClickNext = async () => {
    setPage((currentPage) => currentPage + 1);
  };

  const handleOnClickPrev = async () => {
    setPage((currentPage) => Math.max(currentPage - 1, 1));
  };

  const handleOnSelectOption = (_: string, option: any) => {
    if (!option?.type) return;

    const formFields = form.getFieldsValue(true);

    const { value: selectedFilterValue, type: selectedFilterType } = option;

    const mappedFormValues = Object.keys(formFields).reduce((acc, key) => {
      const formFieldValue = formFields[key as keyof typeof formFields];

      if (key in initialFormValues && selectedFilterType === key) {
        acc[key] = selectedFilterValue;
      } else if (
        key in initialFormValues &&
        formFieldValue !== null &&
        formFieldValue !== undefined
      ) {
        acc[key] = formFieldValue;
      }

      return acc;
    }, {} as Record<string, string>);

    dispatch(setPrimaryFilters(mappedFormValues));
  };

  const handleDateChange = (dates: any) => {
    if (dates) {
      const [playTimeStart, playTimeEnd] = dates;
      form.setFieldsValue({
        playTimeStart: playTimeStart.utc().format("YYYY-MM-DD"),
        playTimeEnd: playTimeEnd.utc().format("YYYY-MM-DD"),
      });
    } else {
      form.setFieldsValue({
        playTimeStart: null,
        playTimeEnd: null,
      });
    }
    const formFields = form.getFieldsValue(true);

    const updatedPrimaryFilters = Object.keys(formFields).reduce((acc, key) => {
      const formFieldValue = formFields[key as keyof typeof formFields];

      if (
        key in initialFormValues &&
        formFieldValue !== null &&
        formFieldValue !== undefined
      ) {
        acc[key] = formFieldValue;
      }

      return acc;
    }, {} as Record<string, string>);

    dispatch(setPrimaryFilters(updatedPrimaryFilters));

    setPage(1);
  };

  const handleOnClickCreate = () => {
    dispatch(
      setActiveModal({
        name: ActiveModalNameEnum.Release,
      })
    );
  };

  const handleOnClearInput = (name: string) => {
    let { ...updatedPrimaryFilters } = primaryFilters;

    updatedPrimaryFilters = Object.keys(updatedPrimaryFilters).reduce(
      (acc: any, key: any) => {
        const keysToRemove: string[] = [name];
        if (!keysToRemove.includes(key)) {
          acc[key] = primaryFilters[key];
        }

        return acc;
      },
      {} as Record<string, string>
    );

    dispatch(setPrimaryFilters(updatedPrimaryFilters));

    setPage(1);
  };

  const handleResetFilters = () => {
    form.resetFields();
    dispatch(resetFilters());
    setPage(1);
  };

  const handleOnHideModal = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  useEffect(() => {
    return () => {
      dispatch(closeModal());
    };
  }, [dispatch]);

  const handleOnCheckboxChange = (event: any, release: Release) => {
    const checked = event.target.checked;
    setChecked((prev) => {
      if (checked) {
        return [...prev, release];
      } else {
        return prev.filter((item) => item.id !== release.id);
      }
    });
  };

  const handleOnCheckAllChange: CheckboxProps["onChange"] = (e) => {
    const allReleases = e.target.checked ? releases : [];

    setChecked(allReleases);

    setCheckAll((prev) => !prev);
  };

  const handleOnConfirmDelete = () => {
    // TODO: Implement delete logic
  };

  return (
    <Container fluid>
      <Form
        requiredMark={false}
        layout="vertical"
        initialValues={initialFormValues}
        form={form}
      >
        <Card>
          <Card.Body>
            <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PageTitle title="Releases" />
              <div>
                <BrandButton
                  variant="outline"
                  disabled={checkedList.length === 0}
                  onClick={() => {
                    /* TODO: Implement bulk delete */
                  }}
                  className="mx-4"
                >
                  <Icon name="trash" size={20} /> Bulk Delete
                </BrandButton>
                <BrandButton
                  variant="outline"
                  disabled={!primaryFilters}
                  onClick={handleResetFilters}
                >
                  <FilterOutlined /> Reset All
                </BrandButton>
              </div>
            </Card.Title>
            <Row>
              <Col>
                <div className="d-flex align-items-center">
                  <Text type="heading">Active Staging Release Versions</Text>
                </div>
                <div className="d-flex align-items-center">
                  <Icon
                    className="me-2"
                    name="krosskast"
                    size={20}
                    useImage={true}
                  />
                  <Text className="d-flex align-items-center mt-2 mb-2">
                    <Text type="heading">KrossKast App: </Text>
                    {activeKrosskastLabel}
                    {data?.active?.versionCode != null
                      ? ` (versionCode ${data.active.versionCode})`
                      : ""}
                  </Text>
                </div>
                <div className="d-flex align-items-center">
                  <Icon
                    className="me-2"
                    name="assistant"
                    size={20}
                    useImage={true}
                  />
                  <Text className="d-flex align-items-center">
                    <Text type="heading">Assistant App: </Text>
                    Not managed by V2 Device Fleet (phase 1)
                  </Text>
                </div>
              </Col>
            </Row>
            {(isLoadingReleases || isFetchingReleases) && <LoadingOverlay />}
            <Row className="my-2">
              <Col sm={3}>
                <FormItem
                  name="type"
                  label="App"
                  className="customSelectWrapper"
                >
                  <FilterSelect<"releases">
                    onSelect={handleOnSelectOption}
                    placeholder="Select app type"
                    name="type"
                    onClear={() => handleOnClearInput("type")}
                    extraData={[
                      {
                        value: "krosskast",
                        label: "Krosskast App",
                        type: "type",
                      },
                      {
                        value: "assistant",
                        label: "Krosskast Assistant App",
                        type: "type",
                      },
                    ]}
                  />
                </FormItem>
              </Col>
              <Col sm={3}>
                <FormItem
                  name="status"
                  label="Status"
                  className="customSelectWrapper"
                >
                  <FilterSelect<"releases">
                    onSelect={handleOnSelectOption}
                    placeholder="Any status"
                    name="status"
                    onClear={() => handleOnClearInput("type")}
                    extraData={[
                      {
                        value: "active",
                        label: "Active",
                        type: "status",
                      },
                      {
                        value: "inactive",
                        label: "Inactive",
                        type: "status",
                      },
                    ]}
                  />
                </FormItem>
              </Col>
              <Col sm={6}>
                <FormItem
                  name="dateRange"
                  validateTrigger="onFinish"
                  label="Filter by Date Range"
                >
                  <DateRangePicker
                    name="dateRange"
                    maxDate={dayjs()}
                    onChange={handleDateChange}
                  />
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col sm={12} lg={{ span: 4, offset: 8 }}>
                <FormItem>
                  <BrandButton
                    className="d-flex align-items-center justify-content-center outline w-100"
                    name="releaseCreate"
                    onClick={handleOnClickCreate}
                    type="button"
                    variant="outline-primary"
                    size="lg"
                  >
                    <FileAddOutlined style={{ fontSize: 22, marginRight: 8 }} />{" "}
                    Create Release
                  </BrandButton>
                </FormItem>
              </Col>
            </Row>
            <Row>
              <Col>
                <Table hover responsive className="legacy-row-height-table">
                  <thead>
                    <tr>
                      <th>
                        <Checkbox
                          checked={checkAll}
                          onChange={handleOnCheckAllChange}
                        />
                      </th>
                      {tableHeaders.map((header, index) =>
                        header.name ? (
                          <th key={index}>
                            <Text type="heading" size="sm">
                              {header.name}
                            </Text>
                          </th>
                        ) : null
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {releases.map((release: Release, index: number) => (
                      <ReleaseRow
                        onChangeCheckbox={handleOnCheckboxChange}
                        checked={checkedList.some(
                          (item) => item.id === release.id
                        )}
                        key={index}
                        {...release}
                      />
                    ))}
                    {releases.length === 0 && <TableRowNoResults colSpan={4} />}
                  </tbody>
                </Table>
              </Col>
            </Row>
            <PaginationRow
              onClickPrevious={handleOnClickPrev}
              handleOnClickNext={handleOnClickNext}
              disableLeft={
                page === 1 || isLoadingReleases || isFetchingReleases
              }
              disableNext={
                isLoadingReleases || isFetchingReleases || releases.length < 25
              }
              page={page}
              onPageChange={(p) => setPage(p)}
            />
          </Card.Body>
        </Card>
        <ReleasesControlPanelModal onHide={handleOnHideModal} />
        <ReleasesDeleteModal
          open={activeModalState.name === ActiveModalNameEnum.ReleaseDelete}
          onCancel={handleOnHideModal}
          onConfirm={handleOnConfirmDelete}
        />
        <ReleaseMakeActiveModal
          open={activeModalState.name === ActiveModalNameEnum.ReleaseMakeActive}
          onCancel={handleOnHideModal}
          onConfirm={handleConfirmMakeActive}
          currentVersion={activeKrosskastLabel}
          nextVersion={
            selectedForActivate
              ? `krosskast-v${selectedForActivate.version}`
              : ""
          }
          appType="krosskast"
        />
        <ReleaseSendProductionModal
          open={
            activeModalState.name ===
            ActiveModalNameEnum.ReleaseSendToProduction
          }
          onCancel={handleOnHideModal}
          onConfirm={() => {
            toast.error(
              "Production promotion is a separately gated Owner operation"
            );
            dispatch(closeModal());
          }}
          currentVersion={activeKrosskastLabel}
          nextVersion={
            selectedForActivate
              ? `krosskast-v${selectedForActivate.version}`
              : ""
          }
        />      </Form>
    </Container>
  );
};

export default ReleasesList;
